import React, { useContext, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import { NotificationType } from "../../constants/global";
import TextField from "@mui/material/TextField";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { bankAccountValidationSchema } from "../../constants/validationSchemas";
import Button from "@mui/material/Button";
import { CircularProgress } from "@mui/material";
import Paper from "@mui/material/Paper";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useMutationUpdatePharmacy } from "../../queries/pharmacy/hooks/useMutationUpdatePharmacy";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { UNIQUE_USER } from "../../queries/pharmacy/pharmacyTypeDefs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { BankBrandMark } from "./MethodAccountCard";

interface PayoutBankAccount {
  id: number;
  accountHolderName?: string | null;
  routingNumber?: string | null;
  last4?: string | null;
  bankName?: string | null;
  status?: string | null;
}

interface PayoutMethodProps {
  bankAccount?: PayoutBankAccount | null;
  pharmacyId: number;
  userId: number;
}

const PayoutMethod: React.FunctionComponent<PayoutMethodProps> = ({
  bankAccount,
  pharmacyId,
  userId,
}) => {
  const { addToast } = useContext(ToasterContext);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [open, setOpen] = useState(false);

  const [updatePharmacy, { loading }] = useMutationUpdatePharmacy({
    refetchQueries: [
      {
        query: UNIQUE_USER,
        variables: { where: { id: userId } },
      },
    ],
    awaitRefetchQueries: true,
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(bankAccountValidationSchema),
    mode: "onChange",
  });

  const handleUpdate = async (values: any) => {
    if (!bankAccount?.id) {
      addToast({
        type: NotificationType.Error,
        title: "No payout bank account is on file yet.",
      });
      return;
    }

    const requestData = {
      BankAccount: {
        update: {
          data: {
            accountHolderName: { set: values.accountHolderName },
            routingNumber: { set: values.routingNumber },
            last4: { set: values.accountNumber.slice(-4) },
          },
          where: {
            id: bankAccount.id,
          },
        },
      },
    };

    try {
      await updatePharmacy({
        variables: {
          data: requestData,
          accountNumber: values.accountNumber,
          where: { id: pharmacyId },
        },
      });
      addToast({
        type: NotificationType.Success,
        title: "Your payout method has been saved and set as the default bank in Stripe.",
      });
      setIsEdit(false);
      reset({
        accountHolderName: "",
        routingNumber: "",
        accountNumber: "",
        confirmAccountNumber: "",
      });
    } catch (error: any) {
      addToast({ type: NotificationType.Error, title: error.message });
    }
  };

  const payoutLabel = bankAccount
    ? `${bankAccount.bankName || "Bank"} · •••• ${bankAccount.last4 || ""}`
    : "No payout bank on file";

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          p: 3,
          m: 0,
          border: "none",
          bgcolor: "transparent",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ArrowForwardIcon sx={{ color: "text.secondary" }} />
          <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Payout Method</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Payout bank used to receive sell-order earnings.
          </Typography>
          </Box>
        </Box>
        <ExpandMoreIcon
          sx={{
            color: "text.secondary",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
          }}
        />
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 3, pb: 3 }}>
      <FormControl fullWidth>
        <InputLabel id="payout-method-select">Payout bank</InputLabel>
        <Select
          labelId="payout-method-select"
          label="Payout bank"
          value={bankAccount ? "current" : ""}
          renderValue={() => payoutLabel}
          displayEmpty
          disabled={!bankAccount || isEdit}
        >
          {bankAccount ? (
            <MenuItem value="current">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <BankBrandMark />
                {payoutLabel}
              </Box>
            </MenuItem>
          ) : (
            <MenuItem value="">
              {payoutLabel}
            </MenuItem>
          )}
        </Select>
      </FormControl>

      {bankAccount && isEdit ? (
        <Box component="form" onSubmit={handleSubmit(handleUpdate)} noValidate sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bank Account Name"
                id="payoutAccountHolderName"
                fullWidth
                required
                helperText={errors.accountHolderName?.message?.toString()}
                {...register("accountHolderName")}
                error={!!errors.accountHolderName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Routing Number"
                id="payoutRoutingNumber"
                fullWidth
                required
                helperText={errors.routingNumber?.message?.toString()}
                {...register("routingNumber")}
                error={!!errors.routingNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Account Number"
                id="payoutAccountNumber"
                fullWidth
                required
                helperText={errors.accountNumber?.message?.toString()}
                {...register("accountNumber")}
                error={!!errors.accountNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm Account Number"
                id="payoutConfirmAccountNumber"
                fullWidth
                required
                helperText={errors.confirmAccountNumber?.message?.toString()}
                {...register("confirmAccountNumber")}
                error={!!errors.confirmAccountNumber}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                reset({
                  accountHolderName: "",
                  routingNumber: "",
                  accountNumber: "",
                  confirmAccountNumber: "",
                });
                setIsEdit(false);
              }}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={loading} sx={{ textTransform: "none" }}>
              Save
              {loading && <CircularProgress sx={{ ml: 1 }} size={14} color="inherit" />}
            </Button>
          </Box>
        </Box>
      ) : bankAccount ? (
        <Box sx={{ mt: 2.5 }}>
          {[
            { label: "Bank", value: bankAccount.bankName },
            { label: "Name", value: bankAccount.accountHolderName },
            { label: "Account #", value: bankAccount.last4 ? `•••• ${bankAccount.last4}` : null },
            { label: "Routing #", value: bankAccount.routingNumber },
          ]
            .filter((row) => row.value)
            .map((row) => (
              <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.75 }}>
                <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.value}</Typography>
              </Box>
            ))}
          <Button
            variant="contained"
            startIcon={<EditOutlinedIcon />}
            onClick={() => {
              reset({
                accountHolderName: "",
                routingNumber: "",
                accountNumber: "",
                confirmAccountNumber: "",
              });
              setIsEdit(true);
            }}
            sx={{ mt: 2, textTransform: "none", fontWeight: 700 }}
          >
            Edit
          </Button>
        </Box>
      ) : null}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5, lineHeight: 1.5 }}>
        {isEdit ? (
          <>
            <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>Warning: </Box>
            Please verify your bank details before saving. Incorrect information may cause payout failures or delays.{" "}
          </>
        ) : null}
        This bank receives deposits from sell orders.
      </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default PayoutMethod;
